<template>
  <div class="kpi-form-view" data-testid="kpi-form-view">
    <div v-if="loading" class="kpi-form-view__status" role="status" aria-live="polite">
      <v-progress-circular indeterminate color="primary" size="32" />
      <span class="kpi-form-view__status-text">Loading KPI...</span>
    </div>
    <div v-else-if="loadFailed" class="kpi-form-view__status" role="alert">
      <span class="kpi-form-view__status-text">This KPI could not be loaded.</span>
    </div>
    <template v-else-if="kpi">
      <DraftKPIForm
        :mode="mode"
        :status="kpi.status"
        :version="kpi.versionNumber || 1"
        :has-pending-changes="kpi.hasPendingChanges"
        :active-tab="activeTab"
        :breadcrumbs-list="breadcrumbs"
        :name="kpi.name"
        :metric="metric"
        :format="kpi.format"
        :decimal-precision="shown.decimalPrecision"
        :targets-type="shown.targetsType"
        :actual-calculation="shown.actualCalculation"
        :less-is-favorable="kpi.lessIsFavorable"
        :description="shown.description"
        :locked-fields="LOCKED_FIELDS"
        :errors="errors"
        :saving="saving"
        :publishing="publishing"
        :proposing="proposing"
        :actions-disabled="busy"
        @update:activeTab="onTabChange"
        @update:decimalPrecision="onEdit('decimalPrecision', $event)"
        @update:targetsType="onEdit('targetsType', $event)"
        @update:actualCalculation="onEdit('actualCalculation', $event)"
        @update:description="onEdit('description', $event)"
        @discard="onDiscard"
        @save-draft="onSave"
        @publish="onPublish"
        @propose-changes="onProposeChanges"
      />
      <DiscardDraftDialog v-model="showDiscardDialog" @onAction="onConfirmDiscard" />
    </template>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, reactive, ref } from "vue";
import DraftKPIForm from "@components/DraftKPIForm/DraftKPIForm.vue";
import DiscardDraftDialog from "@components/DiscardDraftDialog/DiscardDraftDialog.vue";
import { useKpisStore } from "@/store/pinia/kpi";
import { useNotificationStore } from "@/store/pinia/notifications";

const KPI_LIST_PATH = "/performance";
const CURRENT_TAB = 0;
const PROPOSED_TAB = 1;
// Rule 3 locks these across versions, and edit-draft / edit-pending-draft do not accept them.
const LOCKED_FIELDS = ["metric", "format", "favorability"];

const { proxy } = getCurrentInstance();
const kpiStore = useKpisStore();
const notificationStore = useNotificationStore();

const kpi = ref(null);
const loading = ref(true);
const loadFailed = ref(false);
const activeTab = ref(CURRENT_TAB);
const editForm = reactive({
  decimalPrecision: 0,
  targetsType: "Fixed",
  actualCalculation: "Last",
  description: "",
});
const dirty = ref(false);
const saving = ref(false);
const publishing = ref(false);
const proposing = ref(false);
const discarding = ref(false);
const showDiscardDialog = ref(false);

const busy = computed(
  () => saving.value || publishing.value || proposing.value || discarding.value
);

const mode = computed(() => {
  if (kpi.value?.status === "Draft") return "draft";
  if (kpi.value?.hasPendingChanges && activeTab.value === PROPOSED_TAB) return "proposed";
  return "current";
});

const shown = computed(() => (mode.value === "current" ? kpi.value.settings : editForm));

const metric = computed(() => {
  if (!kpi.value) return "";
  if (kpi.value.unitOfMeasure) return kpi.value.unitOfMeasure;
  if (kpi.value.operatingCurrency?.code) return kpi.value.operatingCurrency.code;
  return kpi.value.format === "PERCENT" ? "%" : "";
});

const errors = computed(() =>
  mode.value === "draft" && !metric.value.trim() ? { metric: "Enter unit of measure" } : {}
);

const breadcrumbs = computed(() => [
  { text: "KPIs Catalog", href: KPI_LIST_PATH },
  { text: kpi.value?.name || "KPI", disabled: true },
]);

function resetEditForm(source) {
  Object.assign(editForm, {
    decimalPrecision: source.decimalPrecision,
    targetsType: source.targetsType,
    actualCalculation: source.actualCalculation,
    description: source.description,
  });
  dirty.value = false;
}

function applyKpi(next) {
  kpi.value = next;
  if (next.status === "Draft") {
    resetEditForm(next.settings);
  } else if (next.pending) {
    resetEditForm(next.pending);
  }
}

function errorMessage(error, fallback) {
  const data = error?.response?.data;
  if (typeof data?.detail === "string") return data.detail;
  const first = data && typeof data === "object" ? Object.values(data)[0] : null;
  if (first) return String(Array.isArray(first) ? first[0] : first);
  return fallback;
}

async function run(flag, fallback, action) {
  flag.value = true;
  try {
    await action();
  } catch (error) {
    notificationStore.error(errorMessage(error, fallback));
  } finally {
    flag.value = false;
  }
}

function onEdit(field, value) {
  editForm[field] = value;
  dirty.value = true;
}

function onTabChange(index) {
  activeTab.value = index;
}

async function persistEdits(force = false) {
  if (!dirty.value && !force) return null;
  const id = kpi.value.id;
  const result =
    mode.value === "draft"
      ? await kpiStore.saveKpiDraft(id, editForm)
      : await kpiStore.savePendingDraft(id, editForm);
  dirty.value = false;
  return result;
}

function onSave() {
  return run(saving, "The KPI could not be saved.", async () => {
    const isDraft = mode.value === "draft";
    const pending = { ...editForm };
    const result = await persistEdits(true);
    if (isDraft) {
      applyKpi(result);
    } else {
      applyKpi({ ...result, hasPendingChanges: true, pending });
    }
    notificationStore.success("Draft saved");
  });
}

function onPublish() {
  return run(publishing, "The KPI could not be published.", async () => {
    const isDraft = mode.value === "draft";
    await persistEdits();
    const result = isDraft
      ? await kpiStore.activateKpi(kpi.value.id)
      : await kpiStore.publishPendingDraft(kpi.value.id);
    applyKpi(result);
    activeTab.value = CURRENT_TAB;
    notificationStore.success(isDraft ? "KPI published" : "New version published");
  });
}

function onProposeChanges() {
  return run(proposing, "Changes could not be proposed.", async () => {
    const result = await kpiStore.createPendingDraft(kpi.value.id);
    applyKpi({ ...result, hasPendingChanges: true, pending: { ...result.settings } });
    activeTab.value = PROPOSED_TAB;
  });
}

function onDiscard() {
  if (mode.value === "draft") {
    showDiscardDialog.value = true;
    return Promise.resolve();
  }
  return run(discarding, "The pending draft could not be discarded.", async () => {
    const result = await kpiStore.discardPendingDraft(kpi.value.id);
    applyKpi({ ...result, hasPendingChanges: false, pending: null });
    activeTab.value = CURRENT_TAB;
    notificationStore.success("Pending draft discarded");
  });
}

function onConfirmDiscard() {
  showDiscardDialog.value = false;
  return run(discarding, "The draft could not be discarded.", async () => {
    await kpiStore.deleteKpiDraft(kpi.value.id);
    notificationStore.success("Draft discarded");
    proxy.$router.push(KPI_LIST_PATH);
  });
}

onMounted(async () => {
  try {
    applyKpi(await kpiStore.loadKpi(proxy.$route.params.kpiId));
  } catch {
    loadFailed.value = true;
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.kpi-form-view {
  padding: 24px;
}

.kpi-form-view__status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 200px;
}

.kpi-form-view__status-text {
  font-family: "Inter", sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
}
</style>
